import { useEffect, useRef, useState } from 'react'
import {
  MAX_VOLUME_POSITION,
  amplitudeToDbLabel,
  amplitudeToPosition,
  positionToAmplitude,
} from './volumeMapping'
import { createNadDebug } from '../../../../utils/debugLog'

const volumeHandleLog = createNadDebug('dw:timeline-visual-polish')
const VOLUME_HANDLE_HIT_HEIGHT_PX = 16

interface TimelineVolumeHandleProps {
  clipId: string
  clipType: 'video' | 'audio'
  volume: number
  visible: boolean
  disabled?: boolean
  testId: string
  onPreview?: (clipId: string, volume: number) => void
  onCommit?: (clipId: string, volume: number, baselineVolume: number) => void
}

function volumeFromClientY(clientY: number, rect: DOMRect): number {
  const rawPosition = ((rect.bottom - clientY) / Math.max(1, rect.height)) * MAX_VOLUME_POSITION
  const position = Math.max(0, Math.min(MAX_VOLUME_POSITION, rawPosition))
  return positionToAmplitude(position)
}

export function TimelineVolumeHandle({
  clipId,
  clipType,
  volume,
  visible,
  disabled = false,
  testId,
  onPreview,
  onCommit,
}: TimelineVolumeHandleProps) {
  const baselineRef = useRef(volume)
  const latestVolumeRef = useRef(volume)
  const [dragging, setDragging] = useState(false)
  const [dragVolume, setDragVolume] = useState(volume)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!dragging) {
      setDragVolume(volume)
    }
    latestVolumeRef.current = volume
  }, [dragging, volume])

  const displayedVolume = dragging ? dragVolume : volume
  const displayedPosition = amplitudeToPosition(displayedVolume)
  const lineBottom = `${Math.max(0, Math.min(1, displayedPosition / MAX_VOLUME_POSITION)) * 100}%`
  const label = amplitudeToDbLabel(displayedVolume)
  const isInteractive = visible || dragging

  const previewAt = (clientY: number, rect: DOMRect) => {
    const nextVolume = volumeFromClientY(clientY, rect)
    latestVolumeRef.current = nextVolume
    setDragVolume(nextVolume)
    onPreview?.(clipId, nextVolume)
    return nextVolume
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return
    event.preventDefault()
    event.stopPropagation()
    const rect = rootRef.current?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect()
    baselineRef.current = volume
    setDragging(true)
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // ignore pointer-capture failures
    }
    const nextVolume = previewAt(event.clientY, rect)
    volumeHandleLog({
      event: 'inline-volume-drag-start',
      clipId,
      clipType,
      baseline: Number(volume.toFixed(3)),
      volume: Number(nextVolume.toFixed(3)),
    })
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging || disabled) return
    event.preventDefault()
    event.stopPropagation()
    const rect = rootRef.current?.getBoundingClientRect() ?? event.currentTarget.getBoundingClientRect()
    previewAt(event.clientY, rect)
  }

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    event.preventDefault()
    event.stopPropagation()
    setDragging(false)
    try {
      event.currentTarget.releasePointerCapture(event.pointerId)
    } catch {
      // ignore pointer-capture failures
    }
    onCommit?.(clipId, latestVolumeRef.current, baselineRef.current)
    volumeHandleLog({
      event: 'inline-volume-drag-commit',
      clipId,
      clipType,
      baseline: Number(baselineRef.current.toFixed(3)),
      volume: Number(latestVolumeRef.current.toFixed(3)),
    })
  }

  return (
    <div
      ref={rootRef}
      aria-hidden={!isInteractive}
      className={`absolute inset-x-1 top-1 bottom-1 z-30 transition-opacity ${
        isInteractive ? 'pointer-events-none opacity-100' : 'pointer-events-none opacity-0'
      } ${disabled ? 'cursor-not-allowed' : ''}`}
      data-testid={testId}
      data-volume={displayedVolume.toFixed(3)}
      data-db-label={label}
    >
      <div
        className="absolute left-0 right-0 h-px bg-primary shadow-sm"
        style={{ bottom: lineBottom }}
      />
      <div
        className="absolute right-0 h-2 w-2 translate-x-1/2 translate-y-1/2 rounded-full border border-primary bg-background"
        style={{ bottom: lineBottom }}
      />
      <div
        role="slider"
        aria-label="Clip volume"
        aria-valuemin={0}
        aria-valuemax={MAX_VOLUME_POSITION}
        aria-valuenow={Number(displayedPosition.toFixed(3))}
        aria-disabled={disabled}
        className={`absolute left-0 right-0 ${
          isInteractive && !disabled ? 'pointer-events-auto cursor-ns-resize' : 'pointer-events-none'
        }`}
        style={{
          bottom: `calc(${lineBottom} - ${VOLUME_HANDLE_HIT_HEIGHT_PX / 2}px)`,
          height: `${VOLUME_HANDLE_HIT_HEIGHT_PX}px`,
        }}
        data-testid={`${testId}-hit-target`}
        onMouseDown={(event) => {
          event.preventDefault()
          event.stopPropagation()
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      />
      {dragging && (
        <div
          className="absolute right-1 rounded border border-border bg-background px-1.5 py-0.5 text-xxs font-medium text-foreground shadow-xl"
          style={{ bottom: lineBottom }}
          data-testid={`${testId}-label`}
        >
          {label}
        </div>
      )}
    </div>
  )
}
